import React from 'react';

const AIRobotAnimation = ({ width = 260, height = 260 }) => (
  <svg width={width} height={height} viewBox="0 0 300 220" xmlns="http://www.w3.org/2000/svg">
    <defs>
      {/* Body metallic gradient — top light → mid brand → bottom dark */}
      <linearGradient id="saRobotMetal" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="var(--sa-primary-soft)" />
        <stop offset="45%"  stopColor="var(--sa-primary)" />
        <stop offset="100%" stopColor="var(--sa-dark-accent)" />
      </linearGradient>
      {/* Darker body gradient */}
      <linearGradient id="saRobotMetalDark" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="var(--sa-primary)" />
        <stop offset="100%" stopColor="var(--sa-dark-accent)" />
      </linearGradient>
      {/* Sheen overlay — neutral white highlight */}
      <linearGradient id="saRobotSheen" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor="#ffffff" stopOpacity="0.55" />
        <stop offset="60%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
      {/* Screen background */}
      <radialGradient id="saRobotScreen" cx="50%" cy="38%" r="72%">
        <stop offset="0%"   stopColor="var(--sa-dark-surface)" />
        <stop offset="100%" stopColor="var(--sa-dark-bg)" />
      </radialGradient>
      {/* Eye glow */}
      <radialGradient id="saRobotEyeGlow" cx="50%" cy="45%" r="55%">
        <stop offset="0%"   stopColor="#ffffff" />
        <stop offset="50%"  stopColor="var(--sa-primary-soft)" />
        <stop offset="100%" stopColor="var(--sa-primary)" />
      </radialGradient>
      {/* Ambient halo */}
      <radialGradient id="saRobotHalo" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor="var(--sa-primary)" stopOpacity="0.30" />
        <stop offset="70%"  stopColor="var(--sa-primary)" stopOpacity="0.08" />
        <stop offset="100%" stopColor="var(--sa-primary)" stopOpacity="0" />
      </radialGradient>
      <filter id="saRobotGlow" x="-100%" y="-100%" width="300%" height="300%">
        <feGaussianBlur stdDeviation="3" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>

    <g>
      <animateTransform attributeName="transform" type="translate"
        values="0 0; 0 -8; 0 0" dur="3.6s" repeatCount="indefinite"/>

      {/* Ambient halo */}
      <ellipse cx="150" cy="156" rx="96" ry="98" fill="url(#saRobotHalo)">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="3.6s" repeatCount="indefinite"/>
      </ellipse>

      {/* Antenna */}
      <line x1="150" y1="78" x2="150" y2="52" stroke="url(#saRobotMetalDark)" strokeWidth="4" strokeLinecap="round"/>
      <circle cx="150" cy="48" r="7" fill="url(#saRobotEyeGlow)" filter="url(#saRobotGlow)">
        <animate attributeName="r" values="5;8;5" dur="1.6s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.65;1;0.65" dur="1.6s" repeatCount="indefinite"/>
      </circle>

      {/* Head body */}
      <rect x="92" y="78" width="116" height="92" rx="28" fill="url(#saRobotMetal)"/>
      <rect x="100" y="82" width="100" height="34" rx="20" fill="url(#saRobotSheen)"/>
      <rect x="92" y="78" width="116" height="92" rx="28" fill="none" stroke="var(--sa-primary-soft)" strokeWidth="1.5" opacity="0.55"/>

      {/* Head side buttons */}
      <rect x="82" y="108" width="12" height="32" rx="6" fill="url(#saRobotMetalDark)"/>
      <rect x="206" y="108" width="12" height="32" rx="6" fill="url(#saRobotMetalDark)"/>

      {/* Screen */}
      <rect x="104" y="92" width="92" height="64" rx="20" fill="url(#saRobotScreen)"/>
      <rect x="104" y="92" width="92" height="64" rx="20" fill="none" stroke="var(--sa-primary)" strokeWidth="1" opacity="0.25"/>

      {/* Eyes — blink animation */}
      <g>
        <g>
          <ellipse cx="132" cy="120" rx="11" ry="13" fill="url(#saRobotEyeGlow)" filter="url(#saRobotGlow)"/>
          <ellipse cx="168" cy="120" rx="11" ry="13" fill="url(#saRobotEyeGlow)" filter="url(#saRobotGlow)"/>
          <animateTransform attributeName="transform" type="scale"
            values="1 1; 1 1; 1 0.08; 1 1; 1 1"
            keyTimes="0; 0.45; 0.5; 0.55; 1"
            dur="4.2s" repeatCount="indefinite" additive="sum"/>
          <animateTransform attributeName="transform" type="translate"
            values="0 0; 0 0; 0 110; 0 0; 0 0"
            keyTimes="0; 0.45; 0.5; 0.55; 1"
            dur="4.2s" repeatCount="indefinite" additive="sum"/>
        </g>
      </g>

      {/* Sound bars on screen */}
      <g fill="var(--sa-primary)">
        <rect x="135" y="140" width="6" height="6" rx="2">
          <animate attributeName="height" values="6;10;6" dur="1.2s" repeatCount="indefinite" begin="0s"/>
        </rect>
        <rect x="147" y="140" width="6" height="6" rx="2">
          <animate attributeName="height" values="6;12;6" dur="1.2s" repeatCount="indefinite" begin="0.2s"/>
        </rect>
        <rect x="159" y="140" width="6" height="6" rx="2">
          <animate attributeName="height" values="6;9;6" dur="1.2s" repeatCount="indefinite" begin="0.4s"/>
        </rect>
      </g>

      {/* Body */}
      <rect x="112" y="176" width="76" height="58" rx="20" fill="url(#saRobotMetal)"/>
      <rect x="120" y="180" width="60" height="20" rx="12" fill="url(#saRobotSheen)"/>
      <rect x="112" y="176" width="76" height="58" rx="20" fill="none" stroke="var(--sa-primary-soft)" strokeWidth="1.5" opacity="0.5"/>

      {/* Center chest orb */}
      <circle cx="150" cy="206" r="12" fill="url(#saRobotEyeGlow)" filter="url(#saRobotGlow)">
        <animate attributeName="r" values="10;13;10" dur="2s" repeatCount="indefinite"/>
      </circle>

      {/* Arms */}
      <rect x="92" y="182" width="14" height="40" rx="7" fill="url(#saRobotMetalDark)">
        <animateTransform attributeName="transform" type="rotate"
          values="0 99 184; -14 99 184; 0 99 184" dur="3.6s" repeatCount="indefinite"/>
      </rect>
      <rect x="194" y="182" width="14" height="40" rx="7" fill="url(#saRobotMetalDark)">
        <animateTransform attributeName="transform" type="rotate"
          values="0 201 184; 14 201 184; 0 201 184" dur="3.6s" repeatCount="indefinite"/>
      </rect>

      {/* Chat bubble */}
      <g opacity="0.97">
        <rect x="196" y="84" width="58" height="38" rx="13" fill="var(--sa-primary)" filter="url(#saRobotGlow)"/>
        <path d="M204 120 l-2 12 l16 -10 z" fill="var(--sa-primary)"/>
        <g fill="rgba(255,255,255,0.9)">
          <circle cx="212" cy="103" r="4">
            <animate attributeName="opacity" values="0.2;1;0.2" dur="1.2s" repeatCount="indefinite" begin="0s"/>
          </circle>
          <circle cx="225" cy="103" r="4">
            <animate attributeName="opacity" values="0.2;1;0.2" dur="1.2s" repeatCount="indefinite" begin="0.2s"/>
          </circle>
          <circle cx="238" cy="103" r="4">
            <animate attributeName="opacity" values="0.2;1;0.2" dur="1.2s" repeatCount="indefinite" begin="0.4s"/>
          </circle>
        </g>
      </g>
    </g>
  </svg>
);

export default AIRobotAnimation;

export const AtlasSupportRobot = ({ width = 180, height = 180 }) => (
  <svg width={width} height={height} viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="saAtlasMetal" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="var(--sa-primary-soft)" />
        <stop offset="45%"  stopColor="var(--sa-primary)" />
        <stop offset="100%" stopColor="var(--sa-dark-accent)" />
      </linearGradient>
      <linearGradient id="saAtlasMetalDark" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="var(--sa-primary)" />
        <stop offset="100%" stopColor="var(--sa-dark-accent)" />
      </linearGradient>
      <linearGradient id="saAtlasSheen" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor="#ffffff" stopOpacity="0.55" />
        <stop offset="60%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
      <radialGradient id="saAtlasScreen" cx="50%" cy="38%" r="72%">
        <stop offset="0%"   stopColor="var(--sa-dark-surface)" />
        <stop offset="100%" stopColor="var(--sa-dark-bg)" />
      </radialGradient>
      <radialGradient id="saAtlasEyeGlow" cx="50%" cy="45%" r="55%">
        <stop offset="0%"   stopColor="#ffffff" />
        <stop offset="50%"  stopColor="var(--sa-primary-soft)" />
        <stop offset="100%" stopColor="var(--sa-primary)" />
      </radialGradient>
      <radialGradient id="saAtlasHalo" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor="var(--sa-primary)" stopOpacity="0.25" />
        <stop offset="70%"  stopColor="var(--sa-primary)" stopOpacity="0.06" />
        <stop offset="100%" stopColor="var(--sa-primary)" stopOpacity="0" />
      </radialGradient>
      <filter id="saAtlasGlow" x="-100%" y="-100%" width="300%" height="300%">
        <feGaussianBlur stdDeviation="3" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>

    <g>
      <animateTransform attributeName="transform" type="translate"
        values="0 0; 0 -7; 0 0" dur="3.6s" repeatCount="indefinite"
        calcMode="spline" keySplines="0.45 0 0.55 1; 0.45 0 0.55 1"/>

      {/* Soft halo */}
      <ellipse cx="150" cy="158" rx="92" ry="94" fill="url(#saAtlasHalo)">
        <animate attributeName="opacity" values="0.6;1;0.6" dur="3.6s" repeatCount="indefinite"/>
      </ellipse>

      {/* Antenna */}
      <line x1="150" y1="78" x2="150" y2="52" stroke="url(#saAtlasMetalDark)" strokeWidth="4" strokeLinecap="round"/>
      <circle cx="150" cy="48" r="7" fill="url(#saAtlasEyeGlow)" filter="url(#saAtlasGlow)">
        <animate attributeName="r" values="5;8;5" dur="1.6s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.65;1;0.65" dur="1.6s" repeatCount="indefinite"/>
      </circle>

      {/* HEAD */}
      <rect x="92" y="78" width="116" height="92" rx="28" fill="url(#saAtlasMetal)"/>
      <rect x="100" y="82" width="100" height="34" rx="20" fill="url(#saAtlasSheen)"/>
      <rect x="92" y="78" width="116" height="92" rx="28" fill="none" stroke="var(--sa-primary-soft)" strokeWidth="1.5" opacity="0.55"/>

      {/* Ear nubs */}
      <rect x="82" y="108" width="12" height="32" rx="6" fill="url(#saAtlasMetalDark)"/>
      <rect x="206" y="108" width="12" height="32" rx="6" fill="url(#saAtlasMetalDark)"/>

      {/* Face screen */}
      <rect x="104" y="92" width="92" height="64" rx="20" fill="url(#saAtlasScreen)"/>
      <rect x="104" y="92" width="92" height="64" rx="20" fill="none" stroke="var(--sa-primary)" strokeWidth="1" opacity="0.25"/>

      {/* EYES */}
      <g>
        <ellipse cx="132" cy="120" rx="11" ry="13" fill="url(#saAtlasEyeGlow)" filter="url(#saAtlasGlow)"/>
        <ellipse cx="136" cy="116" rx="3" ry="3" fill="white" opacity="0.55"/>
        <ellipse cx="168" cy="120" rx="11" ry="13" fill="url(#saAtlasEyeGlow)" filter="url(#saAtlasGlow)"/>
        <ellipse cx="172" cy="116" rx="3" ry="3" fill="white" opacity="0.55"/>
        {/* Blink lids */}
        <rect x="119" y="107" width="26" height="26" fill="var(--sa-dark-surface)" opacity="0">
          <animate attributeName="opacity" values="0;0;0;1;1;0;0" keyTimes="0;0.42;0.46;0.50;0.54;0.58;1" dur="5s" repeatCount="indefinite"/>
        </rect>
        <rect x="155" y="107" width="26" height="26" fill="var(--sa-dark-surface)" opacity="0">
          <animate attributeName="opacity" values="0;0;0;1;1;0;0" keyTimes="0;0.42;0.46;0.50;0.54;0.58;1" dur="5s" repeatCount="indefinite"/>
        </rect>
      </g>

      {/* MOUTH — 3 cascading speaking bars */}
      <g fill="var(--sa-dark-accent-light)">
        <rect x="135" y="140" width="6" height="6" rx="2">
          <animate attributeName="height" values="5;11;7;5" dur="1.4s" repeatCount="indefinite" begin="0s"/>
          <animate attributeName="y" values="140;137;139;140" dur="1.4s" repeatCount="indefinite" begin="0s"/>
        </rect>
        <rect x="147" y="140" width="6" height="6" rx="2">
          <animate attributeName="height" values="5;13;8;5" dur="1.4s" repeatCount="indefinite" begin="0.18s"/>
          <animate attributeName="y" values="140;136;139;140" dur="1.4s" repeatCount="indefinite" begin="0.18s"/>
        </rect>
        <rect x="159" y="140" width="6" height="6" rx="2">
          <animate attributeName="height" values="5;10;6;5" dur="1.4s" repeatCount="indefinite" begin="0.36s"/>
          <animate attributeName="y" values="140;137;139;140" dur="1.4s" repeatCount="indefinite" begin="0.36s"/>
        </rect>
      </g>

      {/* BODY */}
      <rect x="112" y="176" width="76" height="58" rx="20" fill="url(#saAtlasMetal)"/>
      <rect x="120" y="180" width="60" height="20" rx="12" fill="url(#saAtlasSheen)"/>
      <rect x="112" y="176" width="76" height="58" rx="20" fill="none" stroke="var(--sa-primary-soft)" strokeWidth="1.5" opacity="0.5"/>

      {/* Body core orb */}
      <circle cx="150" cy="206" r="12" fill="url(#saAtlasEyeGlow)" filter="url(#saAtlasGlow)">
        <animate attributeName="r" values="10;13;10" dur="2.2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.75;1;0.75" dur="2.2s" repeatCount="indefinite"/>
      </circle>

      {/* LEFT ARM */}
      <rect x="92" y="182" width="14" height="42" rx="7" fill="url(#saAtlasMetalDark)">
        <animateTransform attributeName="transform" type="rotate"
          values="0 99 186; -18 99 186; 4 99 186; 0 99 186"
          keyTimes="0; 0.4; 0.75; 1" dur="3.6s" repeatCount="indefinite"
          calcMode="spline" keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"/>
      </rect>

      {/* RIGHT ARM */}
      <rect x="194" y="182" width="14" height="42" rx="7" fill="url(#saAtlasMetalDark)">
        <animateTransform attributeName="transform" type="rotate"
          values="0 201 186; 18 201 186; -4 201 186; 0 201 186"
          keyTimes="0; 0.4; 0.75; 1" dur="3.6s" repeatCount="indefinite"
          calcMode="spline" keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"
          begin="0.5s"/>
      </rect>

      {/* CHAT BUBBLE */}
      <g opacity="0.97">
        <rect x="196" y="84" width="58" height="38" rx="13" fill="var(--sa-primary)" filter="url(#saAtlasGlow)"/>
        <path d="M204 120 l-2 12 l16 -10 z" fill="var(--sa-primary)"/>
        <circle cx="212" cy="103" r="4" fill="rgba(255,255,255,0.9)">
          <animate attributeName="opacity" values="0.2;1;0.2" dur="1.2s" repeatCount="indefinite" begin="0s"/>
          <animate attributeName="r" values="3;4.5;3" dur="1.2s" repeatCount="indefinite" begin="0s"/>
        </circle>
        <circle cx="225" cy="103" r="4" fill="rgba(255,255,255,0.9)">
          <animate attributeName="opacity" values="0.2;1;0.2" dur="1.2s" repeatCount="indefinite" begin="0.22s"/>
          <animate attributeName="r" values="3;4.5;3" dur="1.2s" repeatCount="indefinite" begin="0.22s"/>
        </circle>
        <circle cx="238" cy="103" r="4" fill="rgba(255,255,255,0.9)">
          <animate attributeName="opacity" values="0.2;1;0.2" dur="1.2s" repeatCount="indefinite" begin="0.44s"/>
          <animate attributeName="r" values="3;4.5;3" dur="1.2s" repeatCount="indefinite" begin="0.44s"/>
        </circle>
      </g>
    </g>
  </svg>
);
